/**
 * TaskModal — the SHARED, app-root task detail overlay.
 *
 * Opening a task from ANY productivity page (the person lane, a deep-mode chat
 * message, initiatives, ideas, …) calls useTaskModal().openTask(id). This single
 * modal — mounted once at the app root in App.tsx — then shows that task IN-PLACE
 * as an overlay on whatever page the user is on. We NEVER navigate to /tasks.
 *
 * IMPORTANT: this overlay renders the SAME <TaskDetail/> component the /tasks
 * page (Dashboard), CreateTask and QuickTasks use — NOT a separate, simpler
 * modal. There must be exactly one task detail UI in the app, with its full
 * feature set (live Claude activity, conversation, feature assignment, model
 * selection, start/stop/rerun, …). We only own fetching the task by id (the
 * caller hands us an id, TaskDetail needs the full object) and wiring the
 * close + change-notification plumbing; TaskDetail draws its own backdrop.
 *
 * After any action that changes/removes the task, we dispatch a window
 * `taskmodal:changed` event so the page that opened it (e.g. PersonLane) can
 * refresh its data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getTask } from '../services/api';
import { Task } from '../types';
import { useTheme } from '../context/ThemeContext';
import { useTaskModal } from '../context/TaskModalContext';
import TaskDetail from './TaskDetail';

// Notify any page (e.g. PersonLane) that a task was changed/removed so it can refresh.
const TASK_CHANGED_EVENT = 'taskmodal:changed';
export const onTaskModalChanged = (handler: () => void): (() => void) => {
  const fn = () => handler();
  window.addEventListener(TASK_CHANGED_EVENT, fn);
  return () => window.removeEventListener(TASK_CHANGED_EVENT, fn);
};

// Fire the same change-notification from anywhere a task is created/changed outside this
// modal (e.g. when a chat message spins off a cockpit task) so pages like PersonLane refresh.
export const notifyTaskModalChanged = (): void => {
  window.dispatchEvent(new Event(TASK_CHANGED_EVENT));
};

// Say WHY the task did not load, not just that it did not. The bare "Failed to load task."
// fallback hid the cause (a proxy 502 while the backend restarts, a network blip, a 500
// whose body is plain text) and left the person nothing to act on or report. The server's
// own `detail` still wins whenever it sent one (404 "Task not found", 401 "Sign in").
export const describeTaskLoadError = (err: any, taskId: number): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  const status = err?.response?.status;
  if (status) {
    const text = err.response.statusText ? ` ${err.response.statusText}` : '';
    return `Failed to load task #${taskId}: the server answered ${status}${text}.`;
  }
  if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') {
    return `Failed to load task #${taskId}: the request never reached the server (network error). Check the connection and retry.`;
  }
  return `Failed to load task #${taskId}: ${err?.message || 'unknown error'}.`;
};

// The inner content — fetches the full task for a concrete taskId, then renders
// the shared <TaskDetail/>. Split out so the outer component can gate on
// `openTaskId != null` and remount cleanly per task.
const TaskModalInner: React.FC<{ taskId: number; onClose: () => void }> = ({ taskId, onClose }) => {
  const { theme } = useTheme();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by the Retry button so the fetch effect runs again for the same task.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Tell the opener page to refresh after a change (delete/complete/etc.).
  const notifyChanged = useCallback(() => {
    window.dispatchEvent(new Event(TASK_CHANGED_EVENT));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTask(taskId)
      .then((t) => {
        if (cancelled) return;
        setTask(t);
        setError(null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error('[TaskModal] failed to load task', taskId, err);
        setError(describeTaskLoadError(err, taskId));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, reloadNonce]);

  // Once we have the task, hand off to the real, full-featured detail modal.
  // It owns its own backdrop, Escape-to-close and click-outside handling, so we
  // just give it the task plus our close + change-notification callbacks.
  if (task) {
    return <TaskDetail task={task} onClose={onClose} onTaskUpdate={notifyChanged} />;
  }

  // While loading (or on a fetch error) show a minimal placeholder overlay so the
  // click clearly registered. Matches TaskDetail's backdrop so there's no flash.
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '8px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '12px',
          padding: '24px 28px',
          color: error ? '#ef4444' : theme.textSecondary,
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        {loading ? (
          'Loading task…'
        ) : (
          <div>
            <div>{error || 'Task not found.'}</div>
            {error && (
              <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setReloadNonce((n) => n + 1)}
                  style={{
                    background: theme.primary,
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    background: 'transparent',
                    color: theme.textSecondary,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    padding: '6px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// The app-root mount. Shows the modal whenever a task is open in the global
// context; renders nothing otherwise. Keyed by taskId so switching tasks
// remounts with fresh state.
const TaskModal: React.FC = () => {
  const { openTaskId, closeTask } = useTaskModal();
  if (openTaskId == null) return null;
  return <TaskModalInner key={openTaskId} taskId={openTaskId} onClose={closeTask} />;
};

export default TaskModal;
